import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { ArrowLeft, Eye, EyeOff, RefreshCw, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import authService from '../services/authService';

const initialForm = {
  fullName: '',
  email: '',
  password: '',
  confirmPassword: '',
  verificationCode: '',
};

const UserSignup = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [agreed, setAgreed] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [loadingChallenge, setLoadingChallenge] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const refreshChallenge = useCallback(async () => {
    setLoadingChallenge(true);
    try {
      const response = await api.get('/api/auth/registration-challenge');
      setChallenge(response.data?.data || null);
      setForm((current) => ({ ...current, verificationCode: '' }));
    } catch {
      setChallenge(null);
      toast.error('Unable to load the verification code. Please retry.');
    } finally {
      setLoadingChallenge(false);
    }
  }, []);

  useEffect(() => {
    refreshChallenge();
  }, [refreshChallenge]);

  const change = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.fullName.trim()) return toast.error('Full name is required.');
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters.');
    if (form.password !== form.confirmPassword) return toast.error('Passwords do not match.');
    if (!form.verificationCode.trim()) return toast.error('Enter the visual verification code.');
    if (!agreed) return toast.error('Accept the Terms of Service and Privacy Policy to continue.');
    if (!challenge?.challengeId) return toast.error('Refresh the visual verification code.');

    setLoading(true);
    try {
      await authService.register(form.email, form.password, form.fullName.trim(), {
        challengeId: challenge.challengeId,
        verificationCode: form.verificationCode.trim(),
        agreed,
      });
      toast.success('Account created successfully!');
      navigate('/home');
    } catch (error) {
      toast.error(error.message || 'Registration failed. Please try again.');
      await refreshChallenge();
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid #dee2e6',
    backgroundColor: '#f8fbff',
  };

  return (
    <div className='rainbitx-signup-shell'>
      <Card className='rainbitx-signup-card'>
        <Card.Body className='p-4 p-sm-5'>
          <div className='position-relative text-center mb-4'>
            <Button variant='link' onClick={() => navigate('/home')} className='signup-back p-0' aria-label='Go back'>
              <ArrowLeft size={22} />
            </Button>
            <div className='signup-mark mx-auto mb-3'><ShieldCheck size={27} /></div>
            <h3 className='mb-1 fw-bold text-dark'>Create your account</h3>
            <p className='text-muted small mb-0'>Complete the secure registration check to get started.</p>
          </div>

          <Form onSubmit={submit} noValidate>
            <Form.Group className='mb-3'>
              <Form.Label>Full name</Form.Label>
              <Form.Control name='fullName' value={form.fullName} onChange={change} autoComplete='name' placeholder='Your full name' style={inputStyle} required />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Email address</Form.Label>
              <Form.Control name='email' type='email' value={form.email} onChange={change} autoComplete='email' placeholder='you@example.com' style={inputStyle} required />
              <Form.Text className='text-muted'>This email will be your login username.</Form.Text>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Password</Form.Label>
              <div className='position-relative'>
                <Form.Control name='password' type={showPassword ? 'text' : 'password'} value={form.password} onChange={change} autoComplete='new-password' placeholder='Create a password' style={{ ...inputStyle, paddingRight: 46 }} required />
                <button type='button' className='signup-password-toggle' onClick={() => setShowPassword((value) => !value)} aria-label='Toggle password visibility'>{showPassword ? <EyeOff size={19} /> : <Eye size={19} />}</button>
              </div>
              <Form.Text className='text-muted'>Use at least 8 characters.</Form.Text>
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Confirm password</Form.Label>
              <div className='position-relative'>
                <Form.Control name='confirmPassword' type={showConfirmPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={change} autoComplete='new-password' placeholder='Repeat your password' style={{ ...inputStyle, paddingRight: 46 }} required />
                <button type='button' className='signup-password-toggle' onClick={() => setShowConfirmPassword((value) => !value)} aria-label='Toggle confirm password visibility'>{showConfirmPassword ? <EyeOff size={19} /> : <Eye size={19} />}</button>
              </div>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label>Visual verification code</Form.Label>
              <div className='signup-challenge-row'>
                <Form.Control name='verificationCode' value={form.verificationCode} onChange={change} autoComplete='off' maxLength={4} placeholder='Enter code' className='text-uppercase' style={{ ...inputStyle, letterSpacing: '.18em' }} required />
                <div className='signup-challenge' aria-label={challenge?.code ? `Verification code ${challenge.code}` : 'Loading verification code'}>
                  <span>{loadingChallenge ? '••••' : challenge?.code || '—'}</span>
                  <button type='button' onClick={refreshChallenge} disabled={loadingChallenge} aria-label='Refresh verification code'><RefreshCw size={17} /></button>
                </div>
              </div>
            </Form.Group>

            <Form.Check className='signup-terms mb-4' checked={agreed} onChange={(event) => setAgreed(event.target.checked)} label={<span>I agree to the <Link to='/terms-service'>Terms of Service</Link> and <Link to='/terms-service#privacy-policy'>Privacy Policy</Link>.</span>} />

            <Button type='submit' className='w-100 signup-submit' disabled={loading || loadingChallenge || !challenge}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </Form>

          <p className='text-center text-muted small mt-4 mb-0'>Already have an account? <Link to='/login' className='fw-semibold text-decoration-none'>Log In</Link></p>
        </Card.Body>
      </Card>
      <style>{`
        .rainbitx-signup-shell { min-height: 100vh; display: grid; place-items: center; padding: 28px 16px; background: linear-gradient(145deg, #f8f9fa 0%, #edf4ff 100%); }
        .rainbitx-signup-card { width: 100%; max-width: 540px; border: 0; border-radius: 18px; box-shadow: 0 16px 48px rgba(13, 110, 253, .12); }
        .signup-back { position: absolute; left: 0; top: 4px; color: #212529; }
        .signup-mark { width: 52px; height: 52px; display: grid; place-items: center; border-radius: 14px; color: white; background: linear-gradient(135deg, #0d6efd, #667eea); box-shadow: 0 8px 20px rgba(13, 110, 253, .22); }
        .rainbitx-signup-card .form-label { font-size: 14px; font-weight: 600; color: #212529; }
        .signup-password-toggle { position: absolute; right: 13px; top: 50%; transform: translateY(-50%); border: 0; padding: 0; color: #6c757d; background: transparent; }
        .signup-challenge-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 10px; }
        .signup-challenge { min-width: 154px; min-height: 48px; display: flex; align-items: center; border: 1px solid rgba(13,110,253,.22); border-radius: 10px; overflow: hidden; background: rgba(13,110,253,.07); }
        .signup-challenge span { flex: 1; padding: 0 14px; text-align: center; color: #0d6efd; font: 800 18px monospace; letter-spacing: .2em; user-select: none; }
        .signup-challenge button { align-self: stretch; border: 0; border-left: 1px solid rgba(13,110,253,.18); padding: 0 12px; color: #0d6efd; background: transparent; }
        .signup-terms { padding: 12px 14px 12px 38px; border: 1px solid #e3e8ef; border-radius: 10px; background: #f8fbff; font-size: 14px; }
        .signup-terms a { color: #0d6efd; font-weight: 600; text-decoration: none; }
        .signup-submit { min-height: 48px; border: 0; border-radius: 10px; font-weight: 600; background: linear-gradient(135deg, #0d6efd, #667eea); }
        @media (max-width: 575.98px) { .rainbitx-signup-shell { align-items: start; padding: 12px; } .rainbitx-signup-card { border-radius: 14px; } .signup-challenge-row { grid-template-columns: 1fr; } .signup-challenge { width: 100%; } }
      `}</style>
    </div>
  );
};

export default UserSignup;
